<script>
import axios from '@/utils/axios';
import { Offcanvas } from 'bootstrap';

export default {
  emits: ['updated'],
  data() {
    return {
      mode: 'single',         // 'single' | 'bulk'
      saving: false,
      employees: [],
      leaveTypes: [],
      form: { employee_id: '', leave_type_id: '', year: new Date().getFullYear() },
      errors: {},
      oc: null,
    };
  },
  computed: {
    title() { return this.mode === 'bulk' ? 'Bulk Allocate Balance' : 'Allocate Balance'; },
    employeeOptions() {
      return this.employees.map(e => ({ id: e.id, label: this.empLabel(e) }));
    },
    leaveTypeOptions() {
      return this.leaveTypes.map(t => ({ id: t.id, label: t.code }));
    },
  },
  mounted() {
    this.fetchEmployees();
    this.fetchLeaveTypes();
  },
  methods: {
    async fetchEmployees() {
      try {
        const res = await axios.get('/hrms/employees/dropdown');
        this.employees = res.data.data || [];
      } catch (e) { console.log(e.message); }
    },
    async fetchLeaveTypes() {
      try {
        const res = await axios.get('/hrms/leave-types/dropdown');
        this.leaveTypes = res.data.data || [];
      } catch (e) { console.log(e.message); }
    },
    empLabel(e) {
      return e.full_name || e.name || [e.first_name, e.last_name].filter(Boolean).join(' ') || `#${e.id}`;
    },
    open(mode = 'single') {
      this.mode = mode;
      this.reset();
      this.oc = Offcanvas.getOrCreateInstance(this.$refs.oc);
      this.oc.show();
    },
    close() { this.oc?.hide(); },
    reset() {
      this.form = { employee_id: '', leave_type_id: '', year: new Date().getFullYear() };
      this.errors = {};
    },
    errText(f) {
      const v = this.errors[f];
      if (!v) return '';
      return Array.isArray(v) ? v[0] : v;
    },
    validate() {
      const e = {};
      if (this.mode === 'single' && !this.form.employee_id) e.employee_id = 'Employee is required';
      if (!this.form.leave_type_id) e.leave_type_id = 'Leave type is required';
      if (!this.form.year) e.year = 'Year is required';
      this.errors = e;
      return !Object.keys(e).length;
    },
    async submit() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        let res;
        if (this.mode === 'bulk') {
          res = await axios.post('/hrms/leaves/bulk-allocate-balance', {
            leave_type_id: this.form.leave_type_id,
            year: this.form.year,
          });
        } else {
          res = await axios.post('/hrms/leaves/allocate-balance', {
            employee_id: this.form.employee_id,
            leave_type_id: this.form.leave_type_id,
            year: this.form.year,
          });
        }
        const msg = (typeof res.data.data === 'string' && res.data.data) || res.data.message || 'Balance allocated.';
        this.$swalToast(msg);
        this.$emit('updated');
        this.close();
      } catch (error) {
        if (error.type === 'validation') this.errors = error.errors;
        else this.$swalToast(error.message, 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div ref="oc" class="offcanvas offcanvas-end lv-oc" tabindex="-1" id="hrm_leave_allocate_modal">
    <div class="offcanvas-header">
      <div class="d-flex align-items-center gap-2">
        <span class="lv-oc-ico"><i class="ti ti-wallet"></i></span>
        <h5 class="offcanvas-title mb-0">{{ title }}</h5>
      </div>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas"></button>
    </div>

    <div class="offcanvas-body">
      <p v-if="mode === 'bulk'" class="lv-hint">
        This will allocate balance to all eligible employees at once.
      </p>

      <div v-if="mode === 'single'" class="mb-3">
        <label class="lv-lbl">Employee <span class="text-danger">*</span></label>
        <vue-select v-model="form.employee_id" :options="employeeOptions" label="label" :reduce="e => e.id"
          placeholder="Select employee" :clearable="false"
          :class="{ 'lv-vsel-invalid': errText('employee_id') }" />
        <div v-if="errText('employee_id')" class="lv-err">{{ errText('employee_id') }}</div>
      </div>

    <div class="mb-3">
      <label class="lv-lbl">Leave Type <span class="text-danger">*</span></label>
      <vue-select v-model="form.leave_type_id" :options="leaveTypeOptions" label="label" :reduce="t => t.id"
        placeholder="Select leave type" :clearable="false"
        :class="{ 'lv-vsel-invalid': errText('leave_type_id') }" />
      <div v-if="errText('leave_type_id')" class="lv-err">{{ errText('leave_type_id') }}</div>
    </div>

      <div class="mb-3">
        <label class="lv-lbl">Year <span class="text-danger">*</span></label>
        <input type="number" class="form-control" v-model="form.year" :class="{ 'is-invalid': errText('year') }" />
        <div v-if="errText('year')" class="lv-err">{{ errText('year') }}</div>
      </div>
    </div>

    <div class="lv-oc-footer">
      <button type="button" class="btn btn-light" data-bs-dismiss="offcanvas">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="saving" @click="submit">
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        {{ saving ? 'Allocating…' : (mode === 'bulk' ? 'Bulk Allocate' : 'Allocate') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.lv-oc { width: 520px; max-width: 92vw; }
.offcanvas-header { border-bottom: 1px solid #f1f5f9; padding: 18px 22px; }
.offcanvas-title { font-size: 16px; font-weight: 700; color: #0f172a; }
.lv-oc-ico { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff;
  display: inline-flex; align-items: center; justify-content: center; font-size: 18px; }
.offcanvas-body { padding: 22px; }
.lv-hint { font-size: 12px; color: #64748b; background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px; padding: 10px 12px; margin-bottom: 16px; }
.lv-lbl { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 5px; display: block; }
.form-control, .form-select { border-radius: 10px; border-color: #e2e8f0; font-size: 13.5px; padding: 9px 12px; }
.form-control:focus, .form-select:focus { border-color: #a5b4fc; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
.lv-err { font-size: 11px; color: #dc2626; margin-top: 4px; }
.lv-oc-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 22px; border-top: 1px solid #f1f5f9; margin-top: auto; }
.lv-oc-footer .btn { border-radius: 10px; font-size: 13px; font-weight: 600; padding: 9px 18px; }
.btn-primary { background: linear-gradient(135deg, #4f46e5, #7c3aed); border: none; }
.btn-light { background: #f8fafc; border: 1px solid #e2e8f0; color: #475569; }

/* vue-select styling to match form-control look */
:deep(.vs__dropdown-toggle) {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  min-height: 38px;
  background: #fff;
  padding: 0 4px;
}
:deep(.vs__dropdown-toggle:focus-within) {
  border-color: #a5b4fc;
  box-shadow: 0 0 0 3px rgba(79,70,229,.1);
}
:deep(.vs__search), :deep(.vs__search:focus) { font-size: 13.5px; margin: 0; padding: 6px 4px; }
:deep(.vs__selected) { font-size: 13.5px; color: #1e293b; margin: 4px 2px; }
:deep(.vs__dropdown-option) { font-size: 13.5px; }
:deep(.vs__dropdown-menu) { font-size: 13.5px; }
:deep(.vs__clear), :deep(.vs__open-indicator) { fill: #94a3b8; }

.lv-vsel-invalid :deep(.vs__dropdown-toggle) { border-color: #dc2626; }
</style>